// taken from fa-stage
import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FeatureAppLoader } from '@feature-hub/react';
import { useMediaQuery } from '@oneaudi/feature-app-utils';
import { AssetTypeVideo } from '../AssetTypes';
import { getVideoPlayerConfig, VideoPlayerAssetConfig } from '../utils/getVideoPlayerConfig';
import { FeatureAppContext } from '../context/FeatureAppContext';
import { VideoContainer } from './Styles';

const VIDEO_PLAYER_FA_NAME = 'fa-video-player';
const VIDEO_PLAYER_FA_VERSION = 'v5.5.4';
const VIDEO_PLAYER_FA_ENV = 'prod';
const VIDEO_PLAYER_FA_BASE_URL = `https://${VIDEO_PLAYER_FA_NAME}.cdn.${VIDEO_PLAYER_FA_ENV}.collab.apps.one.audi/${VIDEO_PLAYER_FA_VERSION}`;

const VideoWrapper = styled(VideoContainer)`
  width: auto;
`;

export interface StageVideoProps {
  index: number;
  asset: AssetTypeVideo;
  active: boolean;
}

const StageVideo: React.FC<StageVideoProps> = (props: StageVideoProps) => {
  const { asset, active, index } = props;
  const { videoService, logger } = useContext(FeatureAppContext);
  const [isMounted, setIsMounted] = useState(false);

  const videoRef = useRef<HTMLDivElement>(null);
  const [, setIsInViewport] = useState<boolean | null>(null);

  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const responsiveMediaAsset: VideoPlayerAssetConfig = useMemo(() => {
    const source = isMounted && isMobile ? asset.sourceMobile : asset.source;
    return {
      ...asset,
      src: source,
      srcMobile: asset.sourceMobile,
      isMobile: isMounted && isMobile,
    };
  }, [asset, isMobile, isMounted]);

  const videoPlayerConfig = useMemo(
    () => getVideoPlayerConfig(responsiveMediaAsset),
    [responsiveMediaAsset],
  );

  const [isAutoplay] = useState<boolean>(asset.autoPlay || false);
  const [isPlaying, setIsPlaying] = useState<boolean>(() => isAutoplay);

  const setVideoEvents = () => {
    const video = videoRef.current?.querySelector('video');
    if (!video) {
      logger?.error('video element not found!');
      return;
    }

    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('ended', () => setIsPlaying(false));
  };

  const featureAppId = __FEATURE_APP_NAME__;
  const reactId = useId();
  const videoPlayerId = `${featureAppId}_${VIDEO_PLAYER_FA_NAME}_${reactId}_${index}`;

  useEffect(() => {
    if (!videoRef.current || !window || !videoService) return;

    const rect = videoRef.current.getBoundingClientRect();
    const outTop = rect.top + rect.height < 0;
    const outBottom = rect.top > window.innerHeight;
    const isInViewport_ = !outTop && !outBottom;

    setIsInViewport((wasInViewport) => {
      if (wasInViewport === null) return isInViewport_;

      (async () => {
        try {
          if (wasInViewport && !isInViewport_ && isPlaying) {
            videoService.stop(videoPlayerId);
            setIsPlaying(false);
            setTimeout(() => {
              const videoElement = videoRef.current?.querySelector('video');
              if (videoElement && videoElement.currentTime >= 0) {
                videoElement.currentTime = 0;
              }
            }, 200);
          }

          if (!wasInViewport && isInViewport_ && isAutoplay) {
            await videoService.play(videoPlayerId);
          }
        } catch (error) {
          // ignore autoplay policy violations
          logger?.info('video player: something went wrong', error);
        }
      })();

      return isInViewport_;
    });
  }, [isPlaying, isAutoplay, videoService, videoPlayerId]);

  const autoplay = videoPlayerConfig?.videos?.xs?.autoPlay;

  useEffect(() => {
    if (!videoService || !videoPlayerId) return;

    const videoElement = videoRef.current?.querySelector('video');
    if (!videoElement) return;

    if (!active) {
      videoService.pause(videoPlayerId);
      setIsPlaying(false);
    } else if (autoplay) {
      videoService.play(videoPlayerId);
      setIsPlaying(true);
    }
  }, [active, videoService, videoPlayerId, autoplay]);

  if (!responsiveMediaAsset.src) {
    logger?.error('No video source found');
    return null;
  }
  const serverFileName = process.env.IS_LEGACY ? `app.node.js` : `app-alpha.node.js`;
  const clientFileName = process.env.IS_LEGACY ? 'app.js' : 'app-alpha.js';
  return (
    <VideoWrapper
      isActive={active}
      key="fa-product-highlights-video"
      data-testid="fa-product-highlights-video"
      onLoad={setVideoEvents}
      ref={videoRef}
      className="mediaItem"
    >
      <FeatureAppLoader
        key={isMobile ? 'mobile' : 'desktop'}
        featureAppId={videoPlayerId}
        featureAppName={VIDEO_PLAYER_FA_NAME}
        config={videoPlayerConfig}
        serverSrc={`${VIDEO_PLAYER_FA_BASE_URL}/fh/${serverFileName}`}
        src={`${VIDEO_PLAYER_FA_BASE_URL}/fh/${clientFileName}`}
      />
    </VideoWrapper>
  );
};

export default StageVideo;
